import React from "react";

const INLINE_MARKDOWN_PATTERN =
  /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;

const renderInlineMarkdown = (text, options) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = INLINE_MARKDOWN_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      parts.push(
        <a
          key={`${match.index}-link`}
          href={match[3]}
          target="_blank"
          rel="noreferrer"
          className={options.linkClassName}
        >
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      parts.push(
        <strong key={`${match.index}-strong`} className={options.strongClassName}>
          {match[4]}
        </strong>
      );
    } else if (match[5]) {
      parts.push(
        <em key={`${match.index}-em`} className={options.emClassName}>
          {match[5]}
        </em>
      );
    }

    lastIndex = INLINE_MARKDOWN_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const renderParagraph = (text, key, options) => (
  <p key={key} className={options.paragraphClassName}>
    {renderInlineMarkdown(text, options)}
  </p>
);

const renderHeading = (text, level, key, options) => {
  const Tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";

  return (
    <Tag key={key} className={options.headingClassName}>
      {renderInlineMarkdown(text, options)}
    </Tag>
  );
};

const renderList = (items, ordered, key, options) => {
  const Tag = ordered ? "ol" : "ul";

  return (
    <Tag key={key} className={options.listClassName(ordered)}>
      {items.map((item, index) => (
        <li key={`${key}-${index}`} className={options.listItemClassName}>
          {renderInlineMarkdown(item, options)}
        </li>
      ))}
    </Tag>
  );
};

const SimpleMarkdown = ({
  content,
  className = "",
  paragraphClassName = "mt-4 first:mt-0",
  headingClassName = "mt-6 first:mt-0 text-left font-fordMedium tracking-wide text-fordBlue",
  listClassName,
  listItemClassName = "mt-2 first:mt-0",
  linkClassName = "underline underline-offset-4",
  strongClassName = "font-semibold",
  emClassName = "italic",
}) => {
  if (!content?.trim()) {
    return null;
  }

  const options = {
    paragraphClassName,
    headingClassName,
    listClassName:
      listClassName ||
      ((ordered) =>
        `mt-4 pl-6 text-left ${ordered ? "list-decimal" : "list-disc"}`),
    listItemClassName,
    linkClassName,
    strongClassName,
    emClassName,
  };

  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];

  let paragraphBuffer = [];
  let listBuffer = [];
  let listType = null;

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    blocks.push(
      renderParagraph(paragraphBuffer.join(" "), `p-${blocks.length}`, options)
    );
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!listBuffer.length) return;
    blocks.push(
      renderList(listBuffer, listType === "ol", `list-${blocks.length}`, options)
    );
    listBuffer = [];
    listType = null;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push(
        renderHeading(
          headingMatch[2],
          headingMatch[1].length,
          `heading-${blocks.length}`,
          options
        )
      );
      return;
    }

    const unorderedListMatch = line.match(/^[-*]\s+(.*)$/);
    if (unorderedListMatch) {
      flushParagraph();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listBuffer.push(unorderedListMatch[1]);
      return;
    }

    const orderedListMatch = line.match(/^\d+\.\s+(.*)$/);
    if (orderedListMatch) {
      flushParagraph();
      if (listType && listType !== "ol") {
        flushList();
      }
      listType = "ol";
      listBuffer.push(orderedListMatch[1]);
      return;
    }

    flushList();
    paragraphBuffer.push(line);
  });

  flushParagraph();
  flushList();

  return <div className={className}>{blocks}</div>;
};

export default SimpleMarkdown;
